import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Button,
  Chip,
  Divider
} from '@mui/material'
import { assessmentsApi, questionsApi } from '../../api/assessments'
import { QuestionCard } from '../../components/assessments/QuestionCard'
import type { Assessment, Question } from '../../types/assessments'

// Helper to render icon - supports FontAwesome classes and emojis
const renderIcon = (icon: string) => {
  if (!icon) return '📝'
  // Check if it's a FontAwesome class (starts with 'fa-')
  if (icon.startsWith('fa-') || icon.startsWith('fas ') || icon.startsWith('far ')) {
    return <i className={`fas ${icon}`} style={{ fontSize: '24px' }}></i>
  }
  // Otherwise treat as emoji or text
  return icon
}

export const PreviewAssessmentPage: React.FC = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [assessment, setAssessment] = useState<Assessment | null>(null)
  const [questions, setQuestions] = useState<Question[]>([])

  useEffect(() => {
    const fetchData = async () => {
      if (!assessmentId) return

      try {
        setLoading(true)
        const [assessmentData, questionsData] = await Promise.all([
          assessmentsApi.getById(assessmentId),
          questionsApi.getByAssessmentId(assessmentId)
        ])

        setAssessment(assessmentData)
        setQuestions(questionsData.sort((a, b) => a.order - b.order) as Question[])
      } catch (err: any) {
        setError(err.message || 'Failed to load assessment')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [assessmentId])

  if (loading) {
    return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Box display='flex' justifyContent='center' alignItems='center' minHeight='400px'>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (error || !assessment) {
    return (
      <Container maxWidth='lg' sx={{ py: 4 }}>
        <Alert severity='error'>{error || 'Assessment not found'}</Alert>
      </Container>
    )
  }

  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant='h4' sx={{ fontWeight: 600 }}>
          Preview Assessment
        </Typography>
        <Button variant='outlined' onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>

      <Paper sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2,
              backgroundColor: assessment.color || '#3498db',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
              color: '#fff',
              mr: 3
            }}
          >
            {renderIcon(assessment.icon)}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant='h5' sx={{ fontWeight: 600, mb: 1 }}>
              {assessment.title}
            </Typography>
            <Typography variant='subtitle1' color='text.secondary' sx={{ mb: 1 }}>
              {assessment.subtitle}
            </Typography>
            <Chip
              label={assessment.category}
              sx={{
                backgroundColor: `${assessment.color}20`,
                color: assessment.color,
                fontWeight: 500
              }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {assessment.description && (
          <Box sx={{ mt: 3 }}>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              Description
            </Typography>
            <Typography variant='body1'>{assessment.description}</Typography>
          </Box>
        )}
      </Paper>

      <Typography variant='h5' sx={{ mb: 3, fontWeight: 600 }}>
        Questions ({questions.length})
      </Typography>

      {questions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color='text.secondary'>No questions available</Typography>
        </Paper>
      ) : (
        <Box>
          {questions.map((question) => (
            <QuestionCard
              key={question.id || question.uuid}
              question={question}
              onEdit={() => {}}
              onDelete={() => {}}
              showActions={false}
            />
          ))}
        </Box>
      )}

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          variant='contained'
          size='large'
          sx={{ minWidth: 200 }}
          onClick={() => {
            // Navigate to test start page or trigger test
            console.log('Start test')
          }}
        >
          Start Test
        </Button>
      </Box>
    </Container>
  )
}
